import React, { useEffect, useState } from 'react';
import { Button, Grid, Alert } from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import SimpleDialog from 'components/dialogs/Dialog';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { FormInputDropdown } from 'components/form/FormInputDropdown';
import { FormInputPercentage } from 'components/form/FormInputPercentage';
import i18next from 'i18next';
import { dispatchException, dispatchMessage } from 'helper/snackbar';
import { useDispatch } from 'react-redux';
import yup from 'validation';
import { EOfferCommissionBase } from '__generated__/graphql';
import { ICommissionRate } from 'semshared/offer/commission';

const commissionSchema = yup.object().shape({
  commissionBase: yup.mixed<EOfferCommissionBase>().oneOf(Object.values(EOfferCommissionBase)).required(),
  commissionPercentage: yup.number().required().min(0).max(100),
});

export type CommissionFormType = yup.InferType<typeof commissionSchema>;

interface CommissionDialogProps {
  open: boolean;
  onCancel: () => void;
  onSave: (commission: ICommissionRate) => Promise<void>;
  initialCommission: ICommissionRate;
}

/**
 * Dialog for backoffice users to override offer commission percentage and calculation base.
 */
export const CommissionDialog: React.FC<CommissionDialogProps> = ({ open, onCancel, onSave, initialCommission }) => {
  const dispatch = useDispatch();
  const [validationError, setValidationError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const form = useForm({
    mode: 'all',
    resolver: yupResolver(commissionSchema) as any,
    defaultValues: {
      commissionBase: initialCommission.commissionBase as EOfferCommissionBase,
      commissionPercentage: initialCommission.commissionPercentage * 100,
    },
  });

  useEffect(() => {
    if (open) {
      form.reset({
        commissionBase: initialCommission.commissionBase as EOfferCommissionBase,
        commissionPercentage: initialCommission.commissionPercentage * 100,
      });
      setValidationError(null);
    }
  }, [open, initialCommission, form]);

  const handleSave = () => {
    form.handleSubmit(
      async values => {
        setIsSaving(true);
        try {
          await onSave({
            commissionBase: values.commissionBase,
            commissionPercentage: values.commissionPercentage / 100,
          });
          setValidationError(null);
          dispatchMessage(dispatch, i18next.t('offer-info-commission-saved'));
        } catch (error) {
          dispatchException(dispatch, error);
        } finally {
          setIsSaving(false);
        }
      },
      () => setValidationError(i18next.t('offer-info-commission-validation-error')),
    )();
  };

  const handleCancel = () => {
    form.reset({
      commissionBase: initialCommission.commissionBase as EOfferCommissionBase,
      commissionPercentage: initialCommission.commissionPercentage * 100,
    });
    setValidationError(null);
    onCancel();
  };

  return (
    <SimpleDialog
      title={i18next.t('offer-info-dialog-commission-title')}
      open={open}
      setOpen={() => onCancel()}
      fullWidth
      maxWidth="sm"
      customActions={
        <>
          <Button variant="contained" color="secondary" onClick={handleCancel} startIcon={<CancelIcon />} disabled={isSaving}>
            {i18next.t('cancel')}
          </Button>
          <Button variant="contained" color="primary" onClick={handleSave} startIcon={<SaveIcon />} disabled={isSaving}>
            {i18next.t('apply')}
          </Button>
        </>
      }
    >
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <FormInputDropdown
            name="commissionBase"
            control={form.control}
            label={i18next.t('invoice-commission-base')}
            options={Object.values(EOfferCommissionBase).map(cb => ({
              label: i18next.t(`enums-EOfferCommissionBase-${cb}`),
              value: cb,
            }))}
            required
            disabled={isSaving}
          />
        </Grid>
        <Grid item xs={12}>
          <FormInputPercentage
            name="commissionPercentage"
            control={form.control}
            label={i18next.t('invoice-commission-percentage')}
            required
            disabled={isSaving}
          />
        </Grid>
        {validationError && (
          <Grid item xs={12}>
            <Alert severity="error">{validationError}</Alert>
          </Grid>
        )}
      </Grid>
    </SimpleDialog>
  );
};
